import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {BackendConfiguration} from '@application/configuration/backend-configuration';
import {DateRange} from '@domain/date-range';
import {MachineType} from '@domain/machine/machine-type.enum';
import {CalendarItemGroup} from '@domain/planning/calendar-item-group';
import {ProductionOrderCarpetForPlanningOrder} from '@domain/planning/carpet/production-order-carpet-for-planning-order';
import {NeedsAttentionItem} from '@domain/planning/needs-attention-item';
import {PlanningItem} from '@domain/planning/planning-item';
import {PlanningItemType} from '@domain/planning/planning-item-type.enum';
import {PlanningMachine} from '@domain/planning/planning-machine';
import {PlanningOrder} from '@domain/planning/planning-order';
import {PlanningOrderData} from '@domain/planning/planning-order-data';
import {PlanningOrderDetails} from '@domain/planning/planning-order-details';
import {ProductionOrderTuftingForPlanningOrder} from '@domain/planning/tufting/production-order-tufting-for-planning-order';
import {ProductionOrderWeavingForPlanningOrder} from '@domain/planning/weaving/production-order-weaving-for-planning-order';
import {Subscription} from '@domain/profile/subscription';
import {CreelMap} from '@domain/textile-data/creel/creel-map';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {MachineDetails} from '@infrastructure/http/planning/machine-details';
import {Planning} from '@infrastructure/http/planning/planning';
import {OverviewMachineCreel} from '@presentation/pages/machine-overview/overview-machine-creel';
import {OverviewListCreel} from '@presentation/pages/textile-data/creel/overview/overview-list-creel';
import {OverviewListMachineQuality} from '@presentation/pages/textile-data/machine-quality/overview/overview-list-machine-quality';
import {OverviewListTuftProduct} from '@presentation/pages/textile-data/tuft-product/overview/overview-list-tuft-product';
import {OverviewListWeaveProduct} from '@presentation/pages/textile-data/weave-product/overview/overview-list-weave-product';
import {first, isEqual, last, map} from 'lodash-es';
import {Observable} from 'rxjs';
import {map as rxjsMap} from 'rxjs/operators';

@Injectable()
export class HttpPlanningService implements Planning {
  private httpClient: HttpClient;
  private backendConfiguration: BackendConfiguration;

  public constructor(
    @Inject(AUTHENTICATION) private authentication: Authentication,
    httpClient: HttpClient,
    backendConfiguration: BackendConfiguration
  ) {
    this.httpClient = httpClient;
    this.backendConfiguration = backendConfiguration;
  }

  public getMachines(): Observable<PlanningMachine[]> {
    return this.httpClient.get(`${this.backendConfiguration.getMachineOverviewEndpoint()}planning/machines`).pipe(
      rxjsMap((result: PlanningMachine[]) => {
        return map(result, (machine: any) => {
          return PlanningMachine.fromJSON(machine);
        });
      })
    );
  }

  public getGroupedRunsForPeriod(dateRange: DateRange): Observable<CalendarItemGroup[]> {
    return this.httpClient.get(`${this.backendConfiguration.getMachineOverviewEndpoint()}planning?from=${first(dateRange).getTime()}&to=${last(dateRange).getTime()}`).pipe(
      rxjsMap((result: any) => {
        const currentSubscription: Subscription = this.authentication.getCurrentSubscription();
        return map(result, (calendarItem: any) => {
          return CalendarItemGroup.fromJSON(calendarItem, currentSubscription);
        });
      })
    );
  }

  public getMachineDetails(machineId: number): Observable<MachineDetails> {
    return this.httpClient.get(`${this.backendConfiguration.getMachineOverviewEndpoint()}planning/machines/${machineId}`).pipe(
      rxjsMap((result: MachineDetails) => {
        return {
          machineQuality: result.machineQuality && OverviewListMachineQuality.fromJSON(result.machineQuality),
          creel: result.creel && OverviewMachineCreel.fromJSON(result.creel),
          weaveProduct: result.weaveProduct && OverviewListWeaveProduct.fromJSON(result.weaveProduct),
          tuftProduct: result.tuftProduct && OverviewListTuftProduct.fromJSON(result.tuftProduct)
        };
      })
    );
  }

  public updateGroupedItems(groupedItems: CalendarItemGroup[]): Observable<void> {
    const currentSubscription: Subscription = this.authentication.getCurrentSubscription();
    const itemsToSave = map(groupedItems, (groupedItem: CalendarItemGroup) => groupedItem.toJSON(currentSubscription));
    return this.httpClient.patch<void>(`${this.backendConfiguration.getMachineOverviewEndpoint()}planning`, itemsToSave);
  }

  public getPlanningOrderDetails(id: number): Observable<PlanningOrderDetails> {
    return this.httpClient.get(`${this.backendConfiguration.getMachineOverviewEndpoint()}planning/order/${id}`).pipe(
      rxjsMap((planningOrderDetailsJSON: any) => {
        const machine = PlanningMachine.fromJSON(planningOrderDetailsJSON.machine);
        let orderData: PlanningOrderData;
        if (machine.machineType === MachineType.TUFT) {
          orderData = ProductionOrderTuftingForPlanningOrder.fromJSON(planningOrderDetailsJSON.productionOrderLite);
        } else if (machine.machineType === MachineType.WEAVING_MACHINE) {
          orderData = ProductionOrderWeavingForPlanningOrder.fromJSON(planningOrderDetailsJSON.productionOrderLite);
        } else {
          orderData = ProductionOrderCarpetForPlanningOrder.fromJSON(planningOrderDetailsJSON.productionOrder);
        }
        orderData.yield = planningOrderDetailsJSON.yield;
        const planningOrder = new PlanningOrder(planningOrderDetailsJSON.id, planningOrderDetailsJSON.startDate, planningOrderDetailsJSON.endDate, planningOrderDetailsJSON.efficiency, orderData);

        return {
          machine,
          planningOrder
        };
      })
    );
  }

  public deletePlanningItem(planningItem: PlanningItem): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.backendConfiguration.getMachineOverviewEndpoint()}planning/${isEqual(planningItem.type, PlanningItemType.RUN) ? 'run' : 'maintenance'}/${planningItem.id}`
    );
  }

  public processProductionOrder(id: number): Observable<void> {
    return this.httpClient.patch<void>(`${this.backendConfiguration.getAggregatorEndpoint()}planning/process/${id}`, {});
  }

  public queueProductionOrder(id: number): Observable<void> {
    return this.httpClient.patch<void>(`${this.backendConfiguration.getAggregatorEndpoint()}planning/queue/${id}`, {});
  }

  public processAndQueueProductionOrder(id: number): Observable<void> {
    return this.httpClient.patch<void>(`${this.backendConfiguration.getAggregatorEndpoint()}planning/process-and-queue/${id}`, {});
  }

  public getCreelForRun(runId: number): Observable<OverviewListCreel> {
    return this.httpClient.get(`${this.backendConfiguration.getAggregatorEndpoint()}planning/runs/${runId}/creel`).pipe(rxjsMap((creelJSON: any) => OverviewListCreel.fromJSON(creelJSON)));
  }

  public checkRunCompatibilityWithMachine(runId: number, machineId: number): Observable<number> {
    return this.httpClient.get<number>(`${this.backendConfiguration.getAggregatorEndpoint()}planning/runs/${runId}/is-compatible-machine/${machineId}`);
  }

  public deletePlanningOrder(productionOrderId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.backendConfiguration.getMachineOverviewEndpoint()}planning/production-order/${productionOrderId}`);
  }

  public deletePlanningOrderLite(productionOrderLiteId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.backendConfiguration.getMachineOverviewEndpoint()}planning/production-order-lite/${productionOrderLiteId}`);
  }

  public getCreelMapForRun(runId: number): Observable<CreelMap[]> {
    return this.httpClient.get<CreelMap[]>(`${this.backendConfiguration.getMachineOverviewEndpoint()}planning/run/${runId}/creel-mapping`);
  }

  public getOrdersThatRequireAttention(): Observable<NeedsAttentionItem[]> {
    return this.httpClient
      .get(`${this.backendConfiguration.getAggregatorEndpoint()}planning/require-attention`)
      .pipe(rxjsMap((result: any[]) => map(result, (item: any) => NeedsAttentionItem.fromJSON(item))));
  }
}
